import React, { useState, useEffect, useRef, useMemo } from 'react';
import ConfirmDialog from './ConfirmDialog';
import BaseDialog from './BaseDialog';
import Button from './Button';
import MultiSelect from './MultiSelect';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import FilePreviewDialog from './FilePreviewDialog';
import config from '../../config/environment';

// Helper function to construct full file URL from relative path
const getFileUrl = (filePath) => {
  if (!filePath) return '';

  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get base URL without /api suffix
  const baseURL = config.api.baseURL.replace('/api', '');

  // Construct full URL - Laravel typically serves files from /storage/
  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

  // Construct URL: baseURL/storage/filePath
  return `${baseURL}/storage/${cleanPath}`;
};

/**
 * EditComplaintDialog Component (CVO Only)
 * 
 * A comprehensive dialog for CVO users to edit complaints with full details and history
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {object} complaint - Complaint data object
 * @param {array} complaintStatuses - List of available statuses
 * @param {array} complaintTypes - List of available complaint types
 * @param {array} departments - List of available departments
 * @param {array} officers - List of officers to assign
 * @param {array} priorities - List of priority options
 * @param {array} complaintLogs - Complaint history logs
 * @param {boolean} logsLoading - Loading state for logs
 * @param {string} newNote - Current note input value
 * @param {function} onNoteChange - Callback when note changes
 * @param {function} onAddNote - Callback to add a note
 * @param {function} onSave - Callback when user saves changes
 * @param {function} onStatusChange - Callback when status is changed
 * @param {function} onClose - Callback when dialog closes
 * @param {boolean} showHistory - Whether to show full history section (default: true)
 * @param {boolean} loading - Loading state for async operations
 */
const EditComplaintDialog = ({
  isOpen,
  complaint,
  complaintStatuses = [],
  complaintTypes = [],
  departments = [],
  officers = [],
  priorities = [],
  complaintLogs = [],
  logsLoading = false,
  newNote = '',
  onNoteChange,
  onAddNote,
  onSave,
  onStatusChange,
  onClose,
  showHistory = true,
  loading = false,
}) => {
  const [originalStatus, setOriginalStatus] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [showStatusConfirmDialog, setShowStatusConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const statusSelectRef = useRef(null);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [noteAttachments, setNoteAttachments] = useState([]);

  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: ''
  });

  const handlePreviewFile = (fileOrUrl, name) => {
    if (fileOrUrl instanceof File) {
      const url = URL.createObjectURL(fileOrUrl);
      setPreviewDialog({
        isOpen: true,
        fileUrl: url,
        fileName: fileOrUrl.name,
        fileType: fileOrUrl.type
      });
    } else if (typeof fileOrUrl === 'string') {
      const fullUrl = getFileUrl(fileOrUrl);
      setPreviewDialog({
        isOpen: true,
        fileUrl: fullUrl,
        fileName: name || fileOrUrl.split('/').pop() || 'Document',
        fileType: '' // Type unknown from URL
      });
    }
  };

  const closePreviewDialog = () => {
    if (previewDialog.fileUrl && previewDialog.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewDialog.fileUrl);
    }
    setPreviewDialog({ ...previewDialog, isOpen: false });
  };

  // Helper function to normalize strings for comparison (case-insensitive)
  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).trim().toLowerCase();
  };

  // Helper function to get option value
  const getOptionValue = (item) => {
    return typeof item === 'object' ? item.name : item;
  };

  // Helper function to check if option matches current value
  const isSelected = (optionValue, currentValue) => {
    return normalizeString(optionValue) === normalizeString(currentValue);
  };

  // Initialize original status when complaint changes
  useEffect(() => {
    if (isOpen && complaint) {
      const matchingStatus = complaintStatuses.find(s => isSelected(getOptionValue(s), complaint.status));
      const initialStatus = matchingStatus ? getOptionValue(matchingStatus) : (complaint.status || '');
      setOriginalStatus(initialStatus);
      setCurrentStatus(initialStatus);
    }
  }, [isOpen, complaint, complaintStatuses]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setShowStatusConfirmDialog(false);
      setPendingStatusChange(null);
    }
  }, [isOpen]);

  // Handle save button click
  const handleSaveClick = () => {
    if (!complaint) return;

    // Collect form values for CVO complaint data update
    const assignedDaysValue = document.getElementById('edit-duedays')?.value;
    const assignedDaysNumber = assignedDaysValue ? parseInt(assignedDaysValue, 10) : 30;

    const assignedToValue = document.getElementById('edit-assign')?.value;
    const assignedToNumber = assignedToValue ? parseInt(assignedToValue, 10) : null;

    const formData = {
      assigned_to: assignedToNumber,
      department_name: document.getElementById('edit-dept')?.value || '',
      assigned_day_by_cvo: isNaN(assignedDaysNumber) ? 30 : assignedDaysNumber,
      priority: document.getElementById('edit-priority')?.value || '',
      remark: document.getElementById('edit-remarks')?.value || '',
    };

    if (onSave) {
      onSave(formData);
    }
  };

  // Handle status select change - show confirmation dialog immediately
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;

    if (originalStatus && normalizeString(newStatus) !== normalizeString(originalStatus)) {
      // Status has changed, show confirmation dialog immediately
      setPendingStatusChange(newStatus);
      setShowStatusConfirmDialog(true);
    } else {
      // Status hasn't actually changed, just update the status
      setCurrentStatus(newStatus);
    }
  };

  // Handle status change confirmation
  const handleStatusChangeConfirm = async () => {
    if (pendingStatusChange && onStatusChange) {
      // Update the current status and original status
      setCurrentStatus(pendingStatusChange);
      setOriginalStatus(pendingStatusChange);
      // Update the select element to reflect the new status
      if (statusSelectRef.current) {
        statusSelectRef.current.value = pendingStatusChange;
      }
      // Call the API to update status
      await onStatusChange(complaint.id, pendingStatusChange);
    }
    setShowStatusConfirmDialog(false);
    setPendingStatusChange(null);
  };

  // Handle status change cancellation
  const handleStatusChangeCancel = () => {
    // Reset status select to original value
    if (statusSelectRef.current && originalStatus) {
      statusSelectRef.current.value = originalStatus;
    }
    setCurrentStatus(originalStatus);
    setShowStatusConfirmDialog(false);
    setPendingStatusChange(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setIsHistoryDialogOpen(false);
    }
  }, [isOpen]);

  const sortedLogs = useMemo(() => {
    if (!Array.isArray(complaintLogs)) return [];
    return [...complaintLogs].sort((a, b) => {
      const dateA = new Date(a?.created_at || 0).getTime();
      const dateB = new Date(b?.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [complaintLogs]);

  const previewLogs = useMemo(() => sortedLogs.slice(0, 2), [sortedLogs]);

  if (!isOpen || !complaint) return null;

  // Use separated documents if available, otherwise fall back to documents array
  const supportingDocuments = complaint.supporting_documents || [];
  const generalDocuments = complaint.general_documents || [];
  const hasSeparatedDocs = complaint.supporting_documents !== undefined || complaint.general_documents !== undefined;
  const allDocuments = complaint.documents || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit Complaint: {complaint.complaint_id}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close dialog"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Complainant Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Complainant</label>
              <p className="text-sm text-gray-900">{complaint.name || complaint.complainant}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
              <p className="text-sm text-gray-900">{complaint.mobile}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-sm text-gray-900">{obfuscateEmail(complaint.email)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <p className="text-sm text-gray-900">{complaint.address}</p>
            </div>
          </div>

          {/* Complaint Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Complaint Description</label>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900">{complaint.description || complaint.complaint}</p>
            </div>
          </div>

          {/* Supporting Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Documents:</label>
            <div className="space-y-2">
              {supportingDocuments.length > 0 ? (
                supportingDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  return (
                    <div key={doc.id || index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {fileName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No supporting documents uploaded</p>
              )}
            </div>
          </div>

          {/* General Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">General Documents:</label>
            <div className="space-y-2">
              {generalDocuments.length > 0 ? (
                generalDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  return (
                    <div key={doc.id || index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {fileName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No general documents uploaded</p>
              )}
            </div>
          </div>

          {/* Fallback: All Documents (if separated not available) */}
          {!hasSeparatedDocs && allDocuments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">All Documents:</label>
              <div className="space-y-2">
                {allDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {fileName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Management Fields - CVO Only */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="edit-assign" className="block text-sm font-medium text-gray-700 mb-2">
                Assign Officer
              </label>
              <select
                id="edit-assign"
                defaultValue={complaint.assigned_to || complaint.assignedOfficerId || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                disabled={loading}
              >
                {!complaint.assigned_to && !complaint.assignedOfficerId && (
                  <option value="">Select Officer</option>
                )}
                {officers.filter(o => o.status === 'Active').map(officer => (
                  <option key={officer.id} value={officer.id}>
                    {officer.name} - {officer.designation}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                ref={statusSelectRef}
                id="edit-status"
                defaultValue={(() => {
                  const matchingStatus = complaintStatuses.find(s => isSelected(getOptionValue(s), complaint.status));
                  return matchingStatus ? getOptionValue(matchingStatus) : (complaint.status || '');
                })()}
                onChange={handleStatusChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                disabled={loading}
              >
                {complaintStatuses.map((s, idx) => {
                  const optionValue = getOptionValue(s);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700 mb-2">
                Type (Authority only)
              </label>
              <select
                id="edit-type"
                defaultValue={complaint.type || complaint.assign_complain_type || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled={true}
              >
                {!complaint.type && !complaint.assign_complain_type && (
                  <option value="">Not set by Authority</option>
                )}
                {complaintTypes.map((t, idx) => {
                  const optionValue = getOptionValue(t);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label htmlFor="edit-dept" className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                id="edit-dept"
                defaultValue={(() => {
                  const matchingDept = departments.find(d => isSelected(getOptionValue(d), complaint.department));
                  return matchingDept ? getOptionValue(matchingDept) : (complaint.department || '');
                })()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                disabled={loading}
              >
                {departments.map((d, idx) => {
                  const optionValue = getOptionValue(d);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label htmlFor="edit-duedays" className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Days (by CVO)
              </label>
              <input
                type="number"
                min={0}
                id="edit-duedays"
                defaultValue={complaint.assigned_day_by_cvo}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                placeholder="Enter number of days"
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="edit-priority" className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                id="edit-priority"
                defaultValue={(() => {
                  const matchingPriority = priorities.find(p => isSelected(getOptionValue(p), complaint.priority));
                  return matchingPriority ? getOptionValue(matchingPriority) : (complaint.priority || '');
                })()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                disabled={loading}
              >
                {priorities.map((p, idx) => {
                  const optionValue = getOptionValue(p);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="edit-remarks" className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <textarea
                id="edit-remarks"
                rows={3}
                defaultValue={complaint.remark || complaint.remarks || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                placeholder="Add remarks for the complainer..."
                disabled={loading}
              />
            </div>
          </div>

          {/* Complaint History & Log */}
          {showHistory && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-900">Complaint History & Log</h3>
                {sortedLogs.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsHistoryDialogOpen(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
                  >
                    <span>View full history</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {logsLoading ? (
                    <p className="text-sm text-gray-600">Loading complaint history...</p>
                  ) : sortedLogs.length === 0 ? (
                    <p className="text-sm text-gray-500">No history available for this complaint yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {previewLogs.map((log) => (
                        <HistoryEntryCard key={log.id} log={log} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Add Internal Note */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Add Internal Note</h4>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="note-recipient" className="block text-xs font-medium text-gray-600 mb-1">Send To</label>
                      <MultiSelect
                        id="note-recipient"
                        options={[
                          ...officers.filter(o => o.status === 'Active').map(officer => ({
                            id: `officer-${officer.id}`,
                            name: officer.name
                          })),
                          {
                            id: 'complainant',
                            name: `${complaint.complainant || complaint.name || 'Complainant'} - Complainer`
                          }
                        ]}
                        value={selectedRecipients}
                        onChange={setSelectedRecipients}
                        placeholder="Select recipients..."
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label htmlFor="note-message" className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                      <textarea
                        id="note-message"
                        rows={3}
                        value={newNote}
                        onChange={(e) => onNoteChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 text-sm"
                        placeholder="Enter internal note..."
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label htmlFor="note-attachments" className="block text-xs font-medium text-gray-600 mb-1">
                        Attachments (Optional)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                        <input
                          type="file"
                          id="note-attachments"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                              setNoteAttachments(prev => [...prev, ...files]);
                            }
                            // Reset input to allow selecting same file again
                            e.target.value = '';
                          }}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                          disabled={loading}
                        />
                        <label htmlFor="note-attachments" className="cursor-pointer flex flex-col items-center">
                          <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-xs text-gray-600">Click to upload documents</span>
                          <span className="text-xs text-gray-500 mt-0.5">PDF, DOC, DOCX, JPG, PNG, XLS, XLSX</span>
                        </label>
                      </div>
                      {noteAttachments.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {noteAttachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded text-xs">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-gray-700 truncate">{file.name}</span>
                                <span className="text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handlePreviewFile(file)}
                                className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                                disabled={loading}
                                title="View file"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setNoteAttachments(prev => prev.filter((_, i) => i !== index))}
                                className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0"
                                disabled={loading}
                                title="Remove file"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          onAddNote(noteAttachments, selectedRecipients);
                          setNoteAttachments([]);
                          setSelectedRecipients([]);
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        disabled={loading || (!newNote.trim() && noteAttachments.length === 0)}
                      >
                        {loading && (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        <span>{loading ? 'Sending...' : 'Add to History'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              disabled={loading}
            >
              Close
            </button>
            <button
              onClick={handleSaveClick}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Status Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showStatusConfirmDialog}
        title="Confirm Status Change"
        message="Are you sure you want to change the status as this will be visible to the users (Complainter)?"
        onConfirm={handleStatusChangeConfirm}
        onCancel={handleStatusChangeCancel}
        loading={loading}
        confirmText="Change Status"
        cancelText="Cancel"
        confirmVariant="primary"
      />

      <ComplaintHistoryDialog
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        logs={sortedLogs}
      />

      <FilePreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={closePreviewDialog}
        fileUrl={previewDialog.fileUrl}
        fileName={previewDialog.fileName}
        fileType={previewDialog.fileType}
      />
    </div>
  );
};

export default EditComplaintDialog;

const formatLogDate = (dateString) => {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) {
    return dateString;
  }
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatActionType = (actionType) => {
  if (!actionType) return 'Update';
  return actionType
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const HistoryEntryCard = ({ log }) => {
  const {
    id,
    action_type,
    remark,
    created_at,
    old_value,
    new_value,
    action_by_role,
    action_by,
    complaint_status,
    assigned_to,
  } = log || {};

  const hasValueChange = old_value !== undefined && old_value !== null && new_value !== undefined && new_value !== null;

  return (
    <div key={id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
            {formatActionType(action_type)}
          </span>
          <div className="mt-1 text-sm text-gray-700">
            <span className="font-medium">Remark:</span> {remark || 'No remarks provided.'}
          </div>
        </div>
        <span className="text-xs text-gray-500">{formatLogDate(created_at)}</span>
      </div>
      <HistoryDetailGrid
        hasValueChange={hasValueChange}
        oldValue={old_value}
        newValue={new_value}
        complaintStatus={complaint_status}
        assignedTo={assigned_to}
        actionBy={action_by}
        actionByRole={action_by_role}
      />
    </div>
  );
};

const ComplaintHistoryDialog = ({ isOpen, onClose, logs }) => (
  <BaseDialog
    isOpen={isOpen}
    onClose={onClose}
    title="Complaint History & Log"
    size="large"
  >
    {Array.isArray(logs) && logs.length > 0 ? (
      <div className="space-y-3">
        {logs.map((log) => (
          <HistoryEntryCard key={log.id} log={log} />
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">No history available for this complaint yet.</p>
    )}
    <div className="flex justify-end pt-4">
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </div>
  </BaseDialog>
);

const HistoryDetailGrid = ({
  hasValueChange,
  oldValue,
  newValue,
  complaintStatus,
  assignedTo,
  actionBy,
  actionByRole,
}) => {
  const detailItems = [];

  if (hasValueChange) {
    detailItems.push(
      { label: 'Previous Value', value: oldValue || '—' },
      { label: 'Updated Value', value: newValue || '—' },
    );
  }

  if (complaintStatus) {
    detailItems.push({ label: 'Status', value: complaintStatus });
  }

  if (assignedTo) {
    detailItems.push({ label: 'Assigned To', value: assignedTo });
  }

  const actionByValue = [actionByRole, actionBy].filter(Boolean).join(' - ');
  if (actionByValue) {
    detailItems.push({ label: 'Action By', value: actionByValue });
  }

  if (detailItems.length === 0) {
    return null;
  }

  return (
    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 text-xs text-gray-600">
      {detailItems.map((item, idx) => (
        <div key={`${item.label}-${idx}`}>
          <dt className="font-medium text-gray-700">{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

